import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import { expandToBackendURL, flattenToAppURL } from '@plone/volto/helpers';
import AppNav from '../AppNav/AppNav';
import {
  formatClientNameMessage,
  formatDueSourceMessage,
  formatMatterReferenceMessage,
  formatTokenMessage,
  sharedMessages,
} from '../../utils/uiMessages';

const initialState = {
  generated_on: '',
  management_timezone: '',
  daily_capacity_hours: 3,
  my_today_count: 0,
  my_today_hours: 0,
  my_today_load: 'light',
  carryover_count: 0,
  carryover_hours: 0,
  carryover_load: 'light',
  today_count: 0,
  today_hours: 0,
  today_load: 'light',
  overdue_count: 0,
  overdue_hours: 0,
  in_progress_count: 0,
  in_progress_hours: 0,
  my_today: [],
  carryover: [],
  today: [],
  overdue: [],
  recommended_today: [],
};

const DAILY_CAPACITY_STORAGE_KEY = 'pm.daily_capacity_hours';
const DEFAULT_DAILY_CAPACITY_HOURS = 3;

const messages = defineMessages({
  businessDaysLeft: {
    id: '{count} business days left',
    defaultMessage: '{count} business days left',
  },
  carryover: {
    id: 'Carryover',
    defaultMessage: 'Carryover',
  },
  carryoverFrom: {
    id: 'Carryover from {date}',
    defaultMessage: 'Carryover from {date}',
  },
  carryoverEmpty: {
    id: 'No carryover jobs.',
    defaultMessage: 'No carryover jobs.',
  },
  carryoverNote: {
    id: 'Unfinished pulled work from earlier days. Recommit it, snooze it, or leave it out of today.',
    defaultMessage:
      'Unfinished pulled work from earlier days. Recommit it, snooze it, or leave it out of today.',
  },
  dailyCommitmentView: {
    id: 'Daily commitment view',
    defaultMessage: 'Daily commitment view',
  },
  done: {
    id: 'Done',
    defaultMessage: 'Done',
  },
  doneAndFollowUp: {
    id: 'Done + follow-up',
    defaultMessage: 'Done + follow-up',
  },
  deferredUntil: {
    id: 'Deferred until {date}',
    defaultMessage: 'Deferred until {date}',
  },
  dueToday: {
    id: 'Due Today',
    defaultMessage: 'Due Today',
  },
  dueTodayEmpty: {
    id: 'No jobs due today.',
    defaultMessage: 'No jobs due today.',
  },
  dueTodayNote: {
    id: 'Obligations due today whether or not they have been pulled into Today.',
    defaultMessage:
      'Obligations due today whether or not they have been pulled into Today.',
  },
  generatedOn: {
    id: 'Snapshot date: {date}',
    defaultMessage: 'Snapshot date: {date}',
  },
  inProgress: {
    id: 'In Progress',
    defaultMessage: 'In Progress',
  },
  loadingToday: {
    id: 'Loading today…',
    defaultMessage: 'Loading today…',
  },
  myToday: {
    id: 'My Today',
    defaultMessage: 'My Today',
  },
  myTodayEmpty: {
    id: 'No jobs committed for today yet.',
    defaultMessage: 'No jobs committed for today yet.',
  },
  myTodayNote: {
    id: 'Explicitly pulled work for today.',
    defaultMessage: 'Explicitly pulled work for today.',
  },
  openFullOverview: {
    id: 'Open full overview',
    defaultMessage: 'Open full overview',
  },
  overdue: {
    id: 'Overdue',
    defaultMessage: 'Overdue',
  },
  overdueEmpty: {
    id: 'No overdue jobs.',
    defaultMessage: 'No overdue jobs.',
  },
  overdueNote: {
    id: 'Work that has already missed its internal due date.',
    defaultMessage: 'Work that has already missed its internal due date.',
  },
  pullIntoToday: {
    id: 'Pull into Today',
    defaultMessage: 'Pull into Today',
  },
  requiredDailyHours: {
    id: 'Need {hours}h/day',
    defaultMessage: 'Need {hours}h/day',
  },
  recommendedPullForward: {
    id: 'Recommended pull-forward',
    defaultMessage: 'Recommended pull-forward',
  },
  recommendedPullForwardEmpty: {
    id: 'No suggested pull-forward work today.',
    defaultMessage: 'No suggested pull-forward work today.',
  },
  recommendedPullForwardNote: {
    id: 'Start-by dates that have already arrived.',
    defaultMessage: 'Start-by dates that have already arrived.',
  },
  removeFromToday: {
    id: 'Remove from Today',
    defaultMessage: 'Remove from Today',
  },
  snoozeDay: {
    id: 'Snooze +1d',
    defaultMessage: 'Snooze +1d',
  },
  snoozeWeek: {
    id: 'Snooze +1w',
    defaultMessage: 'Snooze +1w',
  },
  timezone: {
    id: 'Timezone',
    defaultMessage: 'Timezone',
  },
});

function toneClass(prefix, value) {
  return `${prefix} ${prefix}--${(value || 'neutral').replace(/_/g, '-')}`;
}

function SummaryStat({ label, value, meta, tone = 'neutral' }) {
  return (
    <div className={toneClass('patent-dashboard__stat', tone)}>
      <span className="patent-dashboard__stat-label">{label}</span>
      <strong className="patent-dashboard__stat-value">{value}</strong>
      {meta ? <span className="patent-dashboard__stat-meta">{meta}</span> : null}
    </div>
  );
}

async function extractError(response) {
  try {
    const payload = await response.json();
    return (
      payload.error ||
      payload.message ||
      payload.error?.message ||
      `Request failed with status ${response.status}`
    );
  } catch {
    return `Request failed with status ${response.status}`;
  }
}

function normalizeContentPath(url) {
  const appUrl = flattenToAppURL(url || '');
  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  try {
    return new URL(appUrl, origin).pathname;
  } catch {
    return appUrl;
  }
}

function jobEditorUrl(url) {
  return `/job-editor?path=${encodeURIComponent(normalizeContentPath(url))}`;
}

function followUpRegistrationUrl(job) {
  const jobPath = normalizeContentPath(job?.url || '');
  return `/register-job?source_job_path=${encodeURIComponent(jobPath)}`;
}

function displayJobHours(job) {
  return job?.planning_hours ?? job?.estimated_hours ?? 0;
}

function formatHours(value) {
  const normalized = Number(value || 0);
  if (!Number.isFinite(normalized)) {
    return '0';
  }
  return Number.isInteger(normalized) ? String(normalized) : normalized.toFixed(1);
}

function readDailyCapacityHours() {
  if (typeof window === 'undefined') {
    return DEFAULT_DAILY_CAPACITY_HOURS;
  }
  const stored = window.localStorage.getItem(DAILY_CAPACITY_STORAGE_KEY);
  const parsed = Number.parseFloat(stored || '');
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return DEFAULT_DAILY_CAPACITY_HOURS;
  }
  return parsed;
}

function isCurrentToday(job, generatedOn) {
  return Boolean(job?.today_for && generatedOn && job.today_for === generatedOn);
}

function parseISODate(value) {
  if (!value) {
    return null;
  }
  const parts = String(value).split('-').map((item) => parseInt(item, 10));
  if (parts.length !== 3 || parts.some((item) => Number.isNaN(item))) {
    return null;
  }
  return new Date(parts[0], parts[1] - 1, parts[2]);
}

function deferIndicator(job, generatedOn) {
  const deferUntil = parseISODate(job?.defer_until);
  const baseDate = parseISODate(generatedOn);
  if (!deferUntil || !baseDate) {
    return '';
  }
  const deltaDays = Math.round((deferUntil - baseDate) / 86400000);
  if (deltaDays === 1) {
    return 'day';
  }
  if (deltaDays === 7) {
    return 'week';
  }
  return '';
}

function JobList({ items, emptyText, onAction, busyKey, generatedOn }) {
  const intl = useIntl();

  if (!items.length) {
    return <p className="patent-dashboard__empty">{emptyText}</p>;
  }

  return (
    <ul className="patent-dashboard__jobs">
      {items.map((job) => {
        const jobKey = normalizeContentPath(job.url);
        const busy = busyKey === jobKey;
        const activeToday = isCurrentToday(job, generatedOn);
        const deferredState = deferIndicator(job, generatedOn);

        return (
          <li key={`${job.url}-${job.title}`} className="patent-dashboard__job">
            <div className="patent-dashboard__job-row">
              <Link to={jobEditorUrl(job.url)} className="patent-dashboard__job-title">
                {job.title}
              </Link>
              <span className="patent-dashboard__hours">{displayJobHours(job)}h</span>
            </div>
            <div className="patent-dashboard__job-row patent-dashboard__job-row--context">
              <span className="patent-dashboard__context-line">
                {intl.formatMessage(sharedMessages.matterRef)}:{' '}
                {formatMatterReferenceMessage(intl, job.matter_reference_code)}
              </span>
            </div>
            <div className="patent-dashboard__job-row patent-dashboard__job-row--meta">
              <span className="patent-dashboard__meta">
                {formatClientNameMessage(intl, job.client_name)}
              </span>
            </div>
            {job.notes ? (
              <div className="patent-dashboard__job-row patent-dashboard__job-row--notes">
                <div className="patent-dashboard__note">{job.notes}</div>
              </div>
            ) : null}
            <div className="patent-dashboard__chips">
              <span className={toneClass('patent-dashboard__chip', job.priority)}>
                {formatTokenMessage(intl, job.priority)}
              </span>
              <span className={toneClass('patent-dashboard__chip', job.status)}>
                {formatTokenMessage(intl, job.status)}
              </span>
              <span className="patent-dashboard__chip patent-dashboard__chip--due">
                {intl.formatMessage(sharedMessages.internalDue)} {job.internal_due_date}
              </span>
              <span className="patent-dashboard__chip patent-dashboard__chip--source">
                {formatDueSourceMessage(intl, job.due_source)}
              </span>
              {job.legal_due_date ? (
                <span
                  className={`patent-dashboard__chip patent-dashboard__chip--deadline${
                    job.legal_business_days_remaining > 0 &&
                    job.legal_business_days_remaining <= 3
                      ? ' patent-dashboard__chip--deadline-warning'
                      : ''
                  }`}
                >
                  {intl.formatMessage({ id: 'Legal', defaultMessage: 'Legal' })}{' '}
                  {job.legal_due_date}
                </span>
              ) : null}
              {job.client_due_date ? (
                <span className="patent-dashboard__chip patent-dashboard__chip--deadline">
                  {intl.formatMessage({ id: 'Client', defaultMessage: 'Client' })}{' '}
                  {job.client_due_date}
                </span>
              ) : null}
              {job.business_days_remaining > 0 ? (
                <span className="patent-dashboard__chip patent-dashboard__chip--pace">
                  {intl.formatMessage(messages.businessDaysLeft, {
                    count: job.business_days_remaining,
                  })}
                </span>
              ) : null}
              {job.business_days_remaining > 0 && job.required_daily_hours > 0 ? (
                <span className="patent-dashboard__chip patent-dashboard__chip--pace">
                  {intl.formatMessage(messages.requiredDailyHours, {
                    hours: formatHours(job.required_daily_hours),
                  })}
                </span>
              ) : null}
              {job.defer_until ? (
                <span className="patent-dashboard__chip patent-dashboard__chip--source">
                  {intl.formatMessage(messages.deferredUntil, { date: job.defer_until })}
                </span>
              ) : null}
              {job.today_for && !activeToday ? (
                <span className="patent-dashboard__chip patent-dashboard__chip--source">
                  {intl.formatMessage(messages.carryoverFrom, { date: job.today_for })}
                </span>
              ) : null}
            </div>
            <div className="patent-dashboard__job-actions">
              {activeToday ? (
                <button
                  className="patent-entry__ghost patent-dashboard__job-action"
                  type="button"
                  disabled={busy}
                  onClick={() => onAction(job, 'remove_today')}
                >
                  {busy
                    ? intl.formatMessage(sharedMessages.updating)
                    : intl.formatMessage(messages.removeFromToday)}
                </button>
              ) : (
                <button
                  className="patent-dashboard__action patent-dashboard__job-action"
                  type="button"
                  disabled={busy}
                  onClick={() => onAction(job, 'pull_today')}
                >
                  {busy
                    ? intl.formatMessage(sharedMessages.updating)
                    : intl.formatMessage(messages.pullIntoToday)}
                </button>
              )}
              <button
                className={`patent-entry__ghost patent-dashboard__job-action${
                  deferredState === 'day'
                    ? ' patent-dashboard__job-action--deferred-day'
                    : ''
                }`}
                type="button"
                disabled={busy}
                onClick={() => onAction(job, 'snooze_day')}
              >
                {intl.formatMessage(messages.snoozeDay)}
              </button>
              <button
                className={`patent-entry__ghost patent-dashboard__job-action${
                  deferredState === 'week'
                    ? ' patent-dashboard__job-action--deferred-week'
                    : ''
                }`}
                type="button"
                disabled={busy}
                onClick={() => onAction(job, 'snooze_week')}
              >
                {intl.formatMessage(messages.snoozeWeek)}
              </button>
              <button
                className="patent-entry__ghost patent-dashboard__job-action patent-dashboard__job-action--done"
                type="button"
                disabled={busy}
                onClick={() => onAction(job, 'mark_done')}
              >
                {busy
                  ? intl.formatMessage(sharedMessages.updating)
                  : intl.formatMessage(messages.done)}
              </button>
              <button
                className="patent-entry__ghost patent-dashboard__job-action patent-dashboard__job-action--follow-up"
                type="button"
                disabled={busy}
                onClick={() =>
                  onAction(job, 'mark_done', {
                    redirectTo: followUpRegistrationUrl(job),
                  })
                }
              >
                {busy
                  ? intl.formatMessage(sharedMessages.updating)
                  : intl.formatMessage(messages.doneAndFollowUp)}
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export default function TodayPage() {
  const intl = useIntl();
  const token = useSelector((state) => state.userSession?.token);
  const [data, setData] = useState(initialState);
  const [loading, setLoading] = useState(true);
  const [actingOn, setActingOn] = useState('');
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [dailyCapacityHours] = useState(readDailyCapacityHours);

  useEffect(() => {
    document.body.classList.add('patent-dashboard-page');
    return () => {
      document.body.classList.remove('patent-dashboard-page');
    };
  }, []);

  async function fetchSummary() {
    const params = new URLSearchParams();
    params.set('daily_capacity_hours', String(dailyCapacityHours));
    const response = await fetch(
      expandToBackendURL(`/@patent-workload-summary?${params.toString()}`),
      {
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      },
    );
    if (!response.ok) {
      throw new Error(await extractError(response));
    }
    return response.json();
  }

  async function loadSummary() {
    const payload = await fetchSummary();
    setData({ ...initialState, ...payload });
  }

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        const payload = await fetchSummary();
        if (active) {
          setLoadError('');
          setData({ ...initialState, ...payload });
        }
      } catch (loadError) {
        if (active) {
          setLoadError(loadError.message);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [dailyCapacityHours, token]);

  async function onAction(job, action, options = {}) {
    const jobPath = normalizeContentPath(job.url);
    setActingOn(jobPath);
    setActionError('');

    try {
      const response = await fetch(expandToBackendURL('/@job-actions'), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          job_path: jobPath,
          action,
        }),
      });
      if (!response.ok) {
        throw new Error(await extractError(response));
      }
      if (options.redirectTo) {
        window.location.assign(options.redirectTo);
        return;
      }
      await loadSummary();
    } catch (actionError) {
      setActionError(actionError.message);
    } finally {
      setActingOn('');
    }
  }

  if (loading) {
    return (
      <div className="patent-dashboard patent-dashboard--loading">
        {intl.formatMessage(messages.loadingToday)}
      </div>
    );
  }

  if (loadError) {
    return <div className="patent-dashboard patent-dashboard--error">{loadError}</div>;
  }

  return (
    <main className="patent-dashboard">
      <header className="patent-dashboard__hero">
        <div>
          <AppNav currentPath="/today" />
          <p className="patent-dashboard__eyebrow">
            {intl.formatMessage(sharedMessages.today)}
          </p>
          <h1>{intl.formatMessage(messages.dailyCommitmentView)}</h1>
          <p className="patent-dashboard__snapshot">
            {intl.formatMessage(messages.generatedOn, { date: data.generated_on })}
          </p>
          <div className="patent-dashboard__quick-actions">
            <Link className="patent-dashboard__action" to="/register-job">
              {intl.formatMessage(sharedMessages.newJob)}
            </Link>
            <Link className="patent-entry__ghost" to="/overview">
              {intl.formatMessage(messages.openFullOverview)}
            </Link>
          </div>
        </div>
        <div className="patent-dashboard__stats">
          <SummaryStat
            label={intl.formatMessage(messages.carryover)}
            value={`${data.carryover_hours}h`}
            meta={`${data.carryover_count} ${intl.formatMessage({ id: 'jobs', defaultMessage: 'jobs' })}`}
            tone={data.carryover_count ? 'high' : 'light'}
          />
          <SummaryStat
            label={intl.formatMessage(messages.myToday)}
            value={`${data.my_today_hours}h`}
            meta={`${data.my_today_count} ${intl.formatMessage({ id: 'jobs', defaultMessage: 'jobs' })}`}
            tone={data.my_today_load}
          />
          <SummaryStat
            label={intl.formatMessage(messages.overdue)}
            value={`${data.overdue_hours}h`}
            meta={`${data.overdue_count} ${intl.formatMessage({ id: 'jobs', defaultMessage: 'jobs' })}`}
            tone={data.overdue_hours ? 'critical' : 'light'}
          />
          <SummaryStat
            label={intl.formatMessage(messages.dueToday)}
            value={`${data.today_hours}h`}
            meta={`${data.today_count} ${intl.formatMessage({ id: 'jobs', defaultMessage: 'jobs' })}`}
            tone={data.today_load}
          />
          <SummaryStat
            label={intl.formatMessage(messages.inProgress)}
            value={`${data.in_progress_hours}h`}
            meta={`${data.in_progress_count} ${intl.formatMessage({ id: 'jobs', defaultMessage: 'jobs' })}`}
            tone={data.in_progress_count > 3 ? 'high' : 'light'}
          />
        </div>
      </header>

      {actionError ? (
        <p className="patent-entry__error">{actionError}</p>
      ) : null}

      <section className="patent-dashboard__grid">
        {data.carryover_count > 0 ? (
          <article className="patent-dashboard__card patent-dashboard__card--carryover">
            <div className="patent-dashboard__section-header">
              <div>
                <h2>{intl.formatMessage(messages.carryover)}</h2>
                <p className="patent-dashboard__section-note">
                  {intl.formatMessage(messages.carryoverNote)}
                </p>
              </div>
            </div>
            <JobList
              items={data.carryover}
              emptyText={intl.formatMessage(messages.carryoverEmpty)}
              onAction={onAction}
              busyKey={actingOn}
              generatedOn={data.generated_on}
            />
          </article>
        ) : null}

        <article className="patent-dashboard__card">
          <div className="patent-dashboard__section-header">
            <div>
              <h2>{intl.formatMessage(messages.myToday)}</h2>
              <p className="patent-dashboard__section-note">
                {intl.formatMessage(messages.myTodayNote)}
              </p>
            </div>
          </div>
          <JobList
            items={data.my_today}
            emptyText={intl.formatMessage(messages.myTodayEmpty)}
            onAction={onAction}
            busyKey={actingOn}
            generatedOn={data.generated_on}
          />
        </article>

        <article className="patent-dashboard__card">
          <div className="patent-dashboard__section-header">
            <div>
              <h2>{intl.formatMessage(messages.overdue)}</h2>
              <p className="patent-dashboard__section-note">
                {intl.formatMessage(messages.overdueNote)}
              </p>
            </div>
          </div>
          <JobList
            items={data.overdue}
            emptyText={intl.formatMessage(messages.overdueEmpty)}
            onAction={onAction}
            busyKey={actingOn}
            generatedOn={data.generated_on}
          />
        </article>

        <article className="patent-dashboard__card">
          <div className="patent-dashboard__section-header">
            <div>
              <h2>{intl.formatMessage(messages.dueToday)}</h2>
              <p className="patent-dashboard__section-note">
                {intl.formatMessage(messages.dueTodayNote)}
              </p>
            </div>
          </div>
          <JobList
            items={data.today}
            emptyText={intl.formatMessage(messages.dueTodayEmpty)}
            onAction={onAction}
            busyKey={actingOn}
            generatedOn={data.generated_on}
          />
        </article>

        <article className="patent-dashboard__card">
          <div className="patent-dashboard__section-header">
            <div>
              <h2>{intl.formatMessage(messages.recommendedPullForward)}</h2>
              <p className="patent-dashboard__section-note">
                {intl.formatMessage(messages.recommendedPullForwardNote)}
              </p>
            </div>
          </div>
          <JobList
            items={data.recommended_today}
            emptyText={intl.formatMessage(messages.recommendedPullForwardEmpty)}
            onAction={onAction}
            busyKey={actingOn}
            generatedOn={data.generated_on}
          />
        </article>
      </section>

      <section className="patent-dashboard__utility">
        <div className="patent-dashboard__utility-item">
          <span className="patent-dashboard__utility-label">
            {intl.formatMessage(messages.timezone)}
          </span>
          <strong>{data.management_timezone || '—'}</strong>
        </div>
      </section>
    </main>
  );
}
